import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import styled from "styled-components";
import { useTable, usePagination } from "react-table";
import axios from "axios";

const Container = styled.div`
  padding: 10px;

  table {
    width: 100%;
    border: 1px solid black;

    tr {
      :last-child {
        td {
          border-bottom: 0;
        }
      }
    }

    th,
    td {
      margin: 0;
      padding: 5px;
      border-bottom: 1px solid black;
      border-right: 1px solid black;

      :last-child {
        border-right: 0;
      }
    }
  }
`;

const Button = styled.button`
  background-color: #ffffff;
  border: 1px solid rgb(209, 213, 219);
  border-radius: 0.5rem;
  color: #111827;
  font-family: ui-sans-serif, system-ui, -apple-system, system-ui, "Segoe UI",
    Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif,
    "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25rem;
  padding: 0.75rem 1rem;
  text-align: center;
  -webkit-box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  cursor: pointer;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-user-select: none;
  -ms-touch-action: manipulation;
  touch-action: manipulation;

  :hover {
    background-color: #f9fafb;
  }

  :focus {
    outline: 2px solid rgba(0, 0, 0, 0.1);
    outline-offset: 2px;
  }

  :focus-visible {
    -webkit-box-shadow: none;
    box-shadow: none;
  }
`;

function Table({ columns, data, pageCount, setPage }) {
  const {
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    pageOptions,
    gotoPage,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data,
      pageCount,
      manualPagination: true,
      initialState: { pageIndex: 0 },
    },
    usePagination
  );

  useEffect(() => {
    setPage(pageIndex + 1);
  }, [pageIndex, setPage]);

  return (
    <>
      <table>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps()}>{column.render("Header")}</th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {page.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => {
                  return (
                    <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="pagination">
        <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
          {"<<"}
        </button>{" "}
        <button onClick={() => previousPage()} disabled={!canPreviousPage}>
          {"<"}
        </button>{" "}
        <button onClick={() => nextPage()} disabled={!canNextPage}>
          {">"}
        </button>{" "}
        <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
          {">>"}
        </button>{" "}
        <span>
          Page{" "}
          <strong>
            {pageIndex + 1} of {pageOptions.length}
          </strong>{" "}
        </span>
      </div>
    </>
  );
}

const LoanHistory = () => {
  const [searchParams] = useSearchParams();
  const publicId = searchParams.get("query");
  const token = localStorage.getItem("token");
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const columns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
        width: 50,
      },
      {
        Header: "Зээлийн дүн",
        accessor: "amount",
        width: 50,
      },
      {
        Header: "Шимтгэл",
        accessor: "fee",
        width: 50,
      },
      {
        Header: "Хугацаа",
        accessor: "product.days",
        width: 50,
      },
      {
        Header: "Олгосон огноо",
        accessor: (d) => d?.start_date?.replace("T", " "),
        width: 50,
      },
      {
        Header: "Дууссан огноо",
        accessor: (d) => d?.ended_date?.replace("T", " "),
        width: 50,
      },
      {
        Header: "Сунгагдсан эсэх",
        accessor: (r) => r?.is_extension?.toString(),
        width: 50,
      },
      {
        Header: "Дэлгэрэнгүй",
        accessor: "more",
        width: 50,
        Cell: (params) => {
          return (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Link
                to={{
                  pathname: `/closed-loan`,
                  search: `query=${params.row.original.id}`,
                }}
              >
                <Button>Дэлгэрэнгүй</Button>
              </Link>
            </div>
          );
        },
      },
    ],
    []
  );

  useEffect(() => {
    const fetchData = async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/loans?public_id=${publicId}&page=${page}&is_active=false`,
        { headers: { Authorization: "Bearer " + token } }
      );
      setData(response.data.loans);
      setTotalPages(response.data.pages);
    };
    fetchData();
  }, [page, publicId, token]);

  return (
    <>
      <div className="d-flex" style={{ fontSize: 14 }}>
        <div className="card bg-light w-100 m-3">
          <h3 className="card-header">Зээлийн түүхүүд</h3>
          <div className="card-body p-0">
            <Container>
              <Table
                columns={columns}
                data={data ? data : []}
                pageCount={totalPages}
                setPage={setPage}
              />
            </Container>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoanHistory;
